import { ActionIcon } from "PFComponents/action_icon";
import { LoadingDots } from "PFComponents/loading_dots";
import Table from "PFComponents/table";
import { ColumnHelper } from "PFComponents/table/table.types";
import { Typography } from "PFComponents/typography";
import { Attachment } from "PFCore/types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { FormPlaceholderMessage } from "../../../../activities/activity_form/parts/form_placeholder";
import css from "./attachments_selector.module.scss";

type AttachmentsSelectorProps = {
  attachments: Attachment[];
  onChange: (attachments: Attachment[]) => void;
  disabled?: boolean;
};

export const AttachmentsSelector = ({ attachments, onChange, disabled }: AttachmentsSelectorProps) => {
  const [localAttachments, setLocalAttachments] = useState<Attachment[]>(attachments);
  const { t } = useTranslation("activities", { keyPrefix: "form.sections.attachments" });

  useEffect(() => {
    setLocalAttachments(attachments);
  }, [attachments]);

  const handleAttachmentRemove = (attachment: Attachment) => {
    const updatedAttachments = localAttachments.filter(({ id }) => id !== attachment.id);
    setLocalAttachments(updatedAttachments);
    onChange(updatedAttachments);
  };

  const columns = (columnHelper: ColumnHelper<Attachment & { pending?: boolean }>) => [
    columnHelper.accessor("fileName", {
      id: "fileName",
      cell: (info) => (
        <Typography variant="bodyBold" className={css.attachmentName}>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor("pending", {
      id: "pending",
      size: 30,
      cell: (info) => (info.getValue() ? <LoadingDots centered /> : null)
    }),
    columnHelper.accessor((row) => row, {
      id: "remove",
      size: 20,
      cell: (info) => (
        <ActionIcon
          title={t("removeAttachment")}
          onClick={() => handleAttachmentRemove(info.row.original)}
          disabled={disabled}
          color="paletteRed1"
          size="sm"
          name="remove"
        />
      )
    })
  ];

  return (
    <>
      {localAttachments.length > 0 ? (
        <Table<Attachment>
          tableData={localAttachments}
          generateColumns={columns}
          enableSorting={false}
          disableHeader
          disableAutoResizing
        />
      ) : (
        <FormPlaceholderMessage message={t("placeholder")} />
      )}
    </>
  );
};
